import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type MasterCandidate, type RenewalRow, type RenewalDetail, type RenewalGroup, type SignedReadDiff, type SignedReadDifference, type SignedReadItem } from '../lib/api';
import Layout from '../components/Layout';

/**
 * LD1 - CRM > Renewals (spec section 8, his D10). Every live lease and listing Apex brokered, by when to call.
 * ⭐ Ruling 40: the groups, dates and rent come from the server; nothing here computes a date.
 * ⛔ A refusal is said BESIDE its button (the 111th). ⭐ Each row is a card, so it reads at phone width.
 */
const GROUPS: Array<{ key: RenewalGroup; title: string }> = [
  { key: 'call_now', title: 'Call now' }, { key: 'next_6_months', title: 'Next 6 months' }, { key: 'later', title: 'Later' },
  // ⭐ I6a (his answer, the 121st): an agreement whose expiry the read could not find - last, never in Call now, never dropped.
  { key: 'expiry_not_stated', title: 'Expiry not stated' },
];
const SIDE: Record<string, string> = { landlord: 'the landlord', tenant: 'the tenant', both: 'both sides', owner: 'the owner' };

/** A calendar day as he reads it, always with its year - formatted in UTC so no browser zone can move the day. */
export const formatDay = (d: string | null): string => {
  if (!d) return 'not stated';
  const [y, m, day] = d.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const say = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

/** The facts a row opens, in reading order - each with its page, or the reason it is not stated. */
const FACTS: Array<{ key: string; label: string; show: (v: unknown) => string }> = [
  { key: 'dated', label: 'Dated', show: (v) => formatDay(v as string) },
  { key: 'commencement_date', label: 'Commences', show: (v) => formatDay(v as string) },
  { key: 'expiration_date', label: 'Expires', show: (v) => formatDay(v as string) },
  { key: 'new_expiration_date', label: 'New expiration', show: (v) => formatDay(v as string) },
  { key: 'base_rent_monthly', label: 'Base rent', show: (v) => `${money(Number(v))} / month` },
  { key: 'rent_steps', label: 'Rent steps', show: (v) => (Array.isArray(v)
    ? v.map((s: { effective_date: string; monthly: number }) => `${formatDay(s.effective_date)}: ${money(s.monthly)}`).join('; ')
    : `+${(v as { percent: number }).percent}% every ${(v as { every_months: number }).every_months} months from ${formatDay((v as { from: string }).from)}`) },
  { key: 'nnn_monthly', label: 'NNN', show: (v) => `${money(Number(v))} / month` },
  { key: 'security_deposit', label: 'Security deposit', show: (v) => money(Number(v)) },
  { key: 'premises_sf', label: 'Premises', show: (v) => `${Number(v).toLocaleString('en-US')} SF` },
  { key: 'use_text', label: 'Use', show: String },
  { key: 'options_text', label: 'Options', show: String },
  { key: 'listing_rate_text', label: 'Listing terms', show: String },
  { key: 'fee_text', label: 'Fee', show: String },
];

/**
 * DOCTRACK (D15): "The document reads differently: Expires: Nov 30, 2031 vs Oct 31, 2031" - the document's value first,
 * the row's second, each shown the way the card shows that fact. ⛔ Nothing is written over the row.
 */
export const differenceLine = (d: SignedReadDifference): string => {
  const f = FACTS.find((x) => x.key === d.field);
  const show = (v: string) => (f ? f.show(v) : v);
  return `The document reads differently: ${f?.label ?? d.field}: ${show(d.read)} vs ${show(d.onFile)}`;
};

/** One agreement read in the app: what Apply would do, and Apply (or his pick) - never on a read that reads differently. */
function ReadItem({ item, onApplied }: { item: SignedReadItem; onApplied: (full: boolean) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const d = item.decision;
  const apply = async (pick?: string) => {
    setBusy(true); setError(null); setSaid(null);
    try {
      const out = await api.applyRenewalRead(item.filingId, pick) as Record<string, unknown>;
      if (out.action === 'linked' || out.action === 'inserted') { onApplied(true); return; }
      if (out.do === 'not_a_signed_agreement') { setSaid('This read is not a signed agreement.'); setBusy(false); return; }
      onApplied(false);
      setBusy(false);
    } catch (e) { setError(say(e, 'Could not apply it')); setBusy(false); }
  };
  const words = d.do === 'insert'
    ? (item.amends ? `An amendment of ${item.amends.label} - Apply adds it.` : 'Not in Renewals yet - Apply adds it.')
    : d.do === 'link' ? `Matches ${item.matches?.label ?? 'an agreement'} in Renewals - Apply links it.`
    : d.do === 'pick' ? 'It matches more than one agreement in Renewals - which is it?'
    : `Matches ${item.matches?.label ?? 'an agreement'} in Renewals; nothing is written over it.`;
  return (
    <li className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
      <p className="font-medium">{item.title}</p>
      <p>{words}</p>
      {d.do === 'disagrees' && d.differences.map((x) => <p key={x.field} className="text-warning">{differenceLine(x)}</p>)}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {(d.do === 'insert' || d.do === 'link') && (
          <button type="button" disabled={busy} onClick={() => void apply()} className="rounded bg-primary-600 px-3 py-1 text-white disabled:opacity-50">Apply</button>
        )}
        {d.do === 'pick' && d.candidates.map((c) => (
          <button key={c.id} type="button" disabled={busy} onClick={() => void apply(c.id)} className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50">{`Link to ${c.label}`}</button>
        ))}
        {error && <p className="text-primary-500">{error}</p>}
        {said && <p className="text-gray-600">{said}</p>}
      </div>
    </li>
  );
}

/** Opens a signed PDF at its page - the window opened in the press itself (so the browser treats it as his click), pointed
 * at the PDF once the link is signed. ⛔ A refusal is said beside the button (the 111th). */
function PdfButton({ id, label }: { id: string; label: string }) {
  const [error, setError] = useState<string | null>(null);
  const open = async () => {
    setError(null);
    const w = window.open('', '_blank');
    try {
      const { url, page } = await api.getRenewalDocument(id);
      const to = page ? `${url}#page=${page}` : url;
      if (w) w.location.href = to; else window.location.href = to;
    } catch (e) { w?.close(); setError(say(e, 'Could not open the PDF')); }
  };
  return (
    <>
      <button type="button" onClick={() => void open()} className="rounded border border-gray-300 px-3 py-1">{label}</button>
      {error && <p className="text-primary-500">{error}</p>}
    </>
  );
}

/** ⭐ I4 (the 120th review): an amendment - its date, new expiration, option and rent change, each with its page - and its own PDF. */
function AmendmentItem({ a }: { a: Record<string, unknown> }) {
  const cites = (a.cites ?? {}) as Record<string, number>;
  const why = (a.unreadable ?? {}) as Record<string, string>;
  const at = (k: string) => (cites[k] ? ` (p. ${cites[k]})` : '');
  const steps = Array.isArray(a.rent_change) ? (a.rent_change as Array<{ effective_date: string; monthly: number }>) : [];
  return (
    <li data-testid={`amendment-${String(a.id)}`} className="rounded border border-gray-200 p-2">
      <p>{a.dated ? `Amendment dated ${formatDay(a.dated as string)}${at('dated')}` : `Amendment (date not stated${why.dated ? ` - ${why.dated}` : ''})`}</p>
      {/* ⛔ The fix-pass review (the 121st): a new date the read could not make out is SAID, with its reason - it is why the
          card reads "Expiry not stated". */}
      {a.new_expiration_date ? <p>{`New expiration: ${formatDay(a.new_expiration_date as string)}${at('new_expiration_date')}`}</p>
        : why.new_expiration_date ? <p>{`New expiration: not stated (${why.new_expiration_date})`}</p> : null}
      {a.option_exercised != null ? <p>{`Option ${String(a.option_exercised)} exercised${at('option_exercised')}`}</p> : null}
      {steps.map((st) => <p key={st.effective_date}>{`Rent from ${formatDay(st.effective_date)}: ${money(st.monthly)} / month${at('rent_change')}`}</p>)}
      {a.file_id ? <div className="mt-1 flex flex-wrap items-center gap-2"><PdfButton id={String(a.id)} label="Open PDF" /></div> : null}
    </li>
  );
}

function Details({ id }: { id: string }) {
  const [d, setD] = useState<RenewalDetail | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    api.getRenewal(id).then((r) => { if (live) setD(r); }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [id]);
  if (failed) return <p className="text-sm text-gray-600">Could not load this agreement's facts.</p>;
  if (!d) return <p className="text-sm text-gray-500">Loading the facts…</p>;
  const contact = (label: string, c: RenewalDetail['tenant_contact']) => (
    <li>{`${label}: ${c ? [c.name, c.phone, c.email].filter(Boolean).join(' - ') : 'no contact on file'}`}</li>
  );
  // ⛔ M2 (the 121st): a listing has no tenant - "Tenant contact: no contact on file" on every listing read as a gap.
  //    A contact on file is still shown.
  const tenantLine = d.renewal.kind !== 'listing' || d.tenant_contact !== null;
  return (
    <ul className="mt-2 space-y-1 text-sm">
      {/* Found by LOOKING (the 121st): with an amendment, "Expires" here is the date as SIGNED, not today's - say so. */}
      {d.amendments.length > 0 && <li className="font-medium">{d.renewal.kind === 'listing' ? 'The listing as signed' : 'The lease as signed'}</li>}
      {/* ⛔ The fix-pass review (the 121st, Important 1): once an amendment set the expiry the card's button opens THE AMENDMENT -
          the agreement's own PDF (its options, rent and parties) opens here. */}
      {d.amendments.length > 0 && d.facts.file_id ? (
        <li className="flex flex-wrap items-center gap-2"><PdfButton id={id} label={d.renewal.kind === 'listing' ? 'Open the listing PDF' : 'Open the lease PDF'} /></li>
      ) : null}
      {FACTS.map((f) => {
        const v = d.facts[f.key];
        const page = d.facts.cites?.[f.key];
        const why = d.facts.unreadable?.[f.key];
        if (v !== null && v !== undefined) return <li key={f.key}>{`${f.label}: ${f.show(v)}${page ? ` (p. ${page})` : ''}`}</li>;
        if (why) return <li key={f.key}>{`${f.label}: not stated (${why})`}</li>;
        return null;
      })}
      {tenantLine && contact('Tenant contact', d.tenant_contact)}
      {contact(d.renewal.kind === 'listing' ? 'Owner contact' : 'Landlord contact', d.landlord_contact)}
      {d.amendments.length > 0 && (
        <li>
          <p className="font-medium">{`Amendments (${d.amendments.length})`}</p>
          <ul className="mt-1 space-y-1">{d.amendments.map((a) => <AmendmentItem key={String(a.id)} a={a} />)}</ul>
        </li>
      )}
    </ul>
  );
}

function WhichBuilding({ id, onLinked }: { id: string; onLinked: () => void }) {
  const [candidates, setCandidates] = useState<MasterCandidate[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ask = async () => {
    setFailed(false);
    try { setCandidates((await api.getRenewalBuildings(id)).candidates); } catch { setFailed(true); }
  };
  const link = async (masterId: string) => {
    setBusy(masterId); setError(null);
    try { await api.linkRenewalBuilding(id, masterId); onLinked(); }
    catch (e) { setError(say(e, 'Could not link the building')); setBusy(null); }
  };
  if (failed) return <p className="text-sm text-gray-600">Could not look for the building.</p>;
  if (candidates === null) {
    return <button type="button" onClick={() => void ask()} className="rounded border border-gray-300 px-3 py-1 text-sm">Which building is this?</button>;
  }
  if (candidates.length === 0) {
    return (
      <p className="text-sm">No Property Database record matches this address.{' '}
        <Link to="/data-hub" className="text-primary-600 underline">Search the Property Database</Link></p>
    );
  }
  return (
    <ul className="space-y-1 text-sm">
      {candidates.map((c) => (
        <li key={c.id} className="flex flex-wrap items-center gap-2">
          <span>{`Is this ${[c.address, c.city].filter(Boolean).join(', ')}${c.apn ? ` (APN ${c.apn})` : ''}?`}</span>
          <button type="button" disabled={busy !== null} onClick={() => void link(c.id)} className="rounded bg-primary-600 px-3 py-1 text-white disabled:opacity-50">Link</button>
          {error && <p className="text-primary-500">{error}</p>}
        </li>
      ))}
    </ul>
  );
}

function RenewalCard({ r, onChanged, differs = [] }: { r: RenewalRow; onChanged: () => void; differs?: SignedReadDiff[] }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const start = async () => {
    setBusy(true); setStartError(null);
    try {
      const res = await api.startRenewalDeal(r.id);
      navigate(`/crm/deals/${res.deal_id}`);
    } catch (e) { setStartError(say(e, 'Could not start the renewal deal')); setBusy(false); }
  };
  // ⛔ M11 (found by LOOKING, the 121st): the server labels the unit - "Unit Suite 108" doubled a unit the lease names.
  const where = [r.address, r.unit_label, r.city].filter(Boolean).join(', ');
  const unstated = r.group === 'expiry_not_stated';
  // ⭐ I4 (the 120th review): the expiry names its page - the AMENDMENT's when an amendment set it - and the PDF button opens
  //    that document, saying which it is.
  const page = r.expiration_page ? ` (p. ${r.expiration_page})` : '';
  const setBy = r.expiration_set_by;
  const amendmentNamed = (dated: string | null) => (dated ? `the amendment dated ${formatDay(dated)}` : 'an undated amendment');
  const source = !setBy ? page
    : setBy.by === 'option_exercised' ? ` - an option exercised by ${amendmentNamed(setBy.dated)}${page}`
    : ` - set by ${amendmentNamed(setBy.dated)}${page}`;
  // The reason, when there is one worth saying - "not stated" is already what the heading says.
  const unstatedWhy = r.expiration_unreadable === 'not stated' ? '' : ` - ${r.expiration_unreadable ?? 'no reason recorded'}`;
  const fromAmendment = r.expiration_agreement_id !== r.id;
  return (
    <li data-testid="renewal-card" className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{unstated
          ? `Expiry not stated${unstatedWhy}`
          : `Call by ${formatDay(r.call_by)} - ${r.call_by_reason ?? ''}`}</span>
        {r.flag && <span className="rounded bg-warning-wash px-2 text-xs">{r.flag}</span>}
      </div>
      {r.kind !== 'listing' && <p>{`Tenant: ${r.tenant_name ?? 'not stated'}`}</p>}
      <p>{`${r.kind === 'listing' ? 'Owner' : 'Landlord'}: ${r.landlord_name ?? 'not stated'}`}</p>
      <p>{r.master_property_id ? <Link to={`/data-hub/${r.master_property_id}`} className="text-primary-600 underline">{where}</Link> : where}</p>
      {/* LD1 spec section 4: the import links only on ONE exact match - otherwise the card asks. */}
      {!r.master_property_id && <WhichBuilding id={r.id} onLinked={onChanged} />}
      {!unstated && <p>{`Expires ${formatDay(r.current_expiration)}${source}${r.option_deadline ? ` - option deadline ${formatDay(r.option_deadline)}` : ''}`}</p>}
      {/* DOCTRACK (D15): the document the app read says something else - said field by field, never written over the row. */}
      {differs.flatMap((x) => x.differences.map((d) => <p key={`${x.filingId}-${d.field}`} className="text-warning">{differenceLine(d)}</p>))}
      {r.kind !== 'listing' && (
        <p>{r.rent_today !== null ? `Rent today: ${money(r.rent_today)}` : r.rent_starts ? `Rent starts ${formatDay(r.rent_starts)}` : 'Rent today: not stated'}</p>
      )}
      {r.kind !== 'listing' && <p>{`Options: ${r.options_text ?? 'none stated'}`}</p>}
      <p>{`Apex represents ${r.apex_role ? SIDE[r.apex_role] : 'a side not stated'}${r.apex_agents.length ? ` - ${r.apex_agents.join(', ')}` : ''}`}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {r.renewal_deal_id
          ? <Link to={`/crm/deals/${r.renewal_deal_id}`} className="rounded bg-primary-600 px-3 py-1 text-white">Open the renewal deal</Link>
          : <button type="button" disabled={busy} onClick={() => void start()} className="rounded bg-primary-600 px-3 py-1 text-white disabled:opacity-50">Start a renewal deal</button>}
        {startError && <p className="text-primary-500">{startError}</p>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="rounded border border-gray-300 px-3 py-1">Details</button>
        {r.has_document && <PdfButton id={r.expiration_agreement_id} label={fromAmendment ? 'Open the amendment PDF' : 'Open PDF'} />}
      </div>
      {open && <Details id={r.id} />}
    </li>
  );
}

export default function Renewals() {
  const [rows, setRows] = useState<RenewalRow[] | null>(null);
  const [ended, setEnded] = useState(0);
  const [failed, setFailed] = useState(false);
  const [kind, setKind] = useState<'all' | 'lease' | 'listing'>('all');
  const [agent, setAgent] = useState<'all' | 'Robert' | 'Joseph'>('all');

  // DOCTRACK (D15): the agreements read in the app and not in Renewals yet. null = not loaded; 'failed' is said, never an empty list.
  const [reads, setReads] = useState<{ reads: SignedReadItem[]; differs: SignedReadDiff[] } | 'failed' | null>(null);

  // One load path - on mount, and again after a card links its building (the server recomputes the row).
  const load = useCallback(async () => {
    try { const r = await api.listRenewals(); setRows(r.renewals); setEnded(r.ended ?? 0); setFailed(false); } catch { setFailed(true); }
  }, []);
  const loadReads = useCallback(async () => {
    try { const r = await api.listRenewalReads(); setReads({ reads: r.reads ?? [], differs: r.differs ?? [] }); } catch { setReads('failed'); }
  }, []);
  useEffect(() => { void load(); void loadReads(); }, [load, loadReads]);
  const differsFor = (id: string) => (reads && reads !== 'failed' ? reads.differs.filter((x) => x.cardId === id) : []);

  const shown = useMemo(() => (rows ?? []).filter((r) =>
    (kind === 'all' || (kind === 'listing' ? r.kind === 'listing' : r.kind !== 'listing'))
    && (agent === 'all' || r.apex_agents.some((a) => a.toLowerCase().includes(agent.toLowerCase())))), [rows, kind, agent]);

  // ⛔ Inside the app shell like every other CRM page - found by LOOKING (the 120th): it rendered with no sidebar.
  return (
    <Layout>
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">Renewals</h1>
        <p className="text-sm text-gray-600">Every live lease and listing Apex brokered, by when to call.</p>
      </div>
      <div className="flex flex-wrap gap-3 text-sm">
        <label className="flex items-center gap-1">Show
          <select aria-label="Show" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} className="rounded border border-gray-300 bg-white py-1 pl-2 pr-8">
            <option value="all">Leases and listings</option><option value="lease">Leases</option><option value="listing">Listings</option>
          </select>
        </label>
        <label className="flex items-center gap-1">Agent
          <select aria-label="Agent" value={agent} onChange={(e) => setAgent(e.target.value as typeof agent)} className="rounded border border-gray-300 bg-white py-1 pl-2 pr-8">
            <option value="all">Everyone</option><option value="Robert">Robert</option><option value="Joseph">Joseph</option>
          </select>
        </label>
      </div>
      {reads === 'failed' && <p className="text-sm text-gray-600">Could not check for agreements read in the app.</p>}
      {reads && reads !== 'failed' && reads.reads.length > 0 && (
        <section aria-label="Read in the app - not in Renewals yet" className="space-y-2">
          <h2 className="font-semibold">{`Read in the app - not in Renewals yet (${reads.reads.length})`}</h2>
          <ul className="space-y-2">
            {reads.reads.map((x) => (
              <ReadItem key={x.filingId} item={x} onApplied={(full) => { if (full) void load(); void loadReads(); }} />
            ))}
          </ul>
        </section>
      )}
      {failed ? <p className="text-sm text-gray-600">Could not load the renewals.</p>
        : rows === null ? <p className="text-sm text-gray-500">Loading the renewals…</p>
        // ⛔ M3 (the 121st): every agreement having ended is not "none imported".
        : rows.length === 0 ? <p className="text-sm text-gray-600">{ended > 0
          ? `None live - ${ended === 1 ? 'the one signed agreement imported has' : `all ${ended} signed agreements imported have`} ended.`
          : 'None yet - no signed lease or listing has been imported.'}</p>
        : GROUPS.map((g) => {
          const list = shown.filter((r) => r.group === g.key);
          return (
            <section key={g.key} aria-label={g.title} className="space-y-2">
              <h2 className="font-semibold">{`${g.title} (${list.length})`}</h2>
              {list.length ? <ul className="space-y-2">{list.map((r) => <RenewalCard key={r.id} r={r} differs={differsFor(r.id)} onChanged={() => { void load(); void loadReads(); }} />)}</ul>
                : <p className="text-sm text-gray-500">None in this group.</p>}
            </section>
          );
        })}
    </div>
    </Layout>
  );
}
